import type {
  CalculationConfig,
  ClientDemand,
  ComparisonStatFormData,
  DemandComparisonStatisticalResponse
} from 'types/buyer'
import type { Zone } from 'types/match'

import { ENERGY_TYPE } from 'common'
import copy from 'copy.json'
import { FormEvent, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useStore } from 'store'
import styled from 'styled-components'

import Button from 'components/Button'
import ContentHeader from 'components/ContentHeader'
import Filter from 'components/Filter'
import MonthlyDataGraph from 'components/MonthlyDataGraph'
import PageContainer from 'components/PageContainer'
import RangeSlider from 'components/RangeSlider'
import Select from 'components/Select'
import Spinner from 'components/Spinner'

import { ClockIcon, NoDemandsAvailable, SunIcon, WindIcon } from 'icons'
import ConsumptionDetailsModal from 'modals/ConsumptionDetailsModal'
import { clientPanelPaths } from 'paths'
import YearSummary from '../../matches/YearSummary'
import ZonesModal from '../../matches/ZonesModal'

const text = copy.page.client_panel.calculator
const calcDefaultEnergy = (number: number) => Math.round((number / 2) * 10) / 10

export default function MatchDetailsScreen() {
  const [currentUserDemands, setCurrentUserDemands] = useState<ClientDemand[] | null>(null)
  const [windEnergy, setWindEnergy] = useState(0)
  const [solarEnergy, setSolarEnergy] = useState(0)
  const [maxPower, setMaxPower] = useState<CalculationConfig | null>(null)
  const [businessId, setBusinessId] = useState<string | null>(null)
  const [matchData, setMatchData] = useState<DemandComparisonStatisticalResponse | null>(null)
  const [zones, setZones] = useState<Zone[]>([])
  const [isZonesModalOpen, setIsZonesModalOpen] = useState(false)
  const [consumptionDetailsModalOpen, setConsumptionDetailsModalOpen] = useState(false)

  const [loading, setLoading] = useState(false)

  const { api } = useStore()
  const navigate = useNavigate()

  const openZonesModal = (zones: Zone[]) => {
    setZones(Object.values(zones))
    setIsZonesModalOpen(true)
  }

  const submitRecalculation = async (
    event?: FormEvent<HTMLFormElement>,
    id?: string,
    energy?: ComparisonStatFormData
  ) => {
    if (event) {
      event.preventDefault()
    }

    if (!(businessId || id)) return

    setLoading(true)

    const bId = (businessId || id) as string
    const formData: ComparisonStatFormData = {
      solar_installation_power: energy?.solar_installation_power || solarEnergy,
      wind_installation_power: energy?.wind_installation_power || windEnergy
    }

    const response = await api.generateDemandComparisonStatistical(bId, formData)
    setMatchData(response || null)
    setLoading(false)
  }

  const handleDemandChange = async (id: string) => {
    setBusinessId(id)
    let response = await api.getCalculationConfig(id)

    if (response) {
      setMaxPower(response)
      setSolarEnergy(calcDefaultEnergy(response.max_solar_installation_power))
      setWindEnergy(calcDefaultEnergy(response.max_wind_installation_power))
      const energy = {
        solar_installation_power: calcDefaultEnergy(response.max_solar_installation_power),
        wind_installation_power: calcDefaultEnergy(response.max_wind_installation_power)
      }
      submitRecalculation(undefined, id, energy)
    }
  }

  useEffect(() => {
    const getBuyerDemands = async () => {
      const response = await api.getClientResources('demand', {
        page_number: 0,
        statuses: ['ACTIVE']
      })
      setCurrentUserDemands(response?.content || null)
      if (response?.content.length) {
        handleDemandChange(response.content[0].business_id)
      }
    }
    getBuyerDemands()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const areEnergyValuesValid = windEnergy > 0 || solarEnergy > 0

  const shouldDisplaySpinner =
    currentUserDemands === null || (currentUserDemands.length > 0 && (!matchData || !maxPower))

  if (shouldDisplaySpinner) return <Spinner size={60} centered style={{ color: 'var(--white)' }} />

  return (
    <>
      <Content>
        <ContentHeader>
          <h1>{copy.components.clientPanelNavigation.match}</h1>

          {businessId && (
            <Button secondary onClick={() => setConsumptionDetailsModalOpen(true)}>
              <ClockIcon /> {text.consumption_button}
            </Button>
          )}
        </ContentHeader>

        {currentUserDemands.length === 0 && (
          <NoDemands>
            <NoDemandsAvailable style={{ maxWidth: '100%' }} />

            <h2>{text.no_demands.heading}</h2>

            <p>{text.no_demands.paragraph}</p>

            <Button onClick={() => navigate(clientPanelPaths.add_demand)}>
              {text.no_demands.button}
            </Button>
          </NoDemands>
        )}

        {!!currentUserDemands.length && (
          <Filter>
            <label>
              {text.chosen_demand}:
              <Select
                defaultValue={currentUserDemands[0].business_id}
                name="demand"
                onChange={event => {
                  handleDemandChange(event.target.value)
                }}
              >
                {currentUserDemands?.map(demand => (
                  <option value={demand.business_id} key={demand.business_id}>
                    {text.yearly_power_consumption}: {demand.yearly_power_consumption}{' '}
                    {copy.common.MWh}, {text.electric_energy_meter_number}:{' '}
                    {demand.electric_energy_meter_number}
                  </option>
                ))}
              </Select>
            </label>
          </Filter>
        )}
        {matchData && maxPower && (
          <Info>
            <OfferInfoWrapper>
              <h3>{text.heading_range_sliders}</h3>

              <form onSubmit={submitRecalculation}>
                <SliderTitle>
                  <SunIcon color={'var(--green)'} />
                  {ENERGY_TYPE.SOLAR}
                </SliderTitle>

                <RangeSlider
                  id="solar_energy"
                  min={0}
                  max={maxPower.max_solar_installation_power}
                  value={solarEnergy}
                  step={0.1}
                  label={` ${copy.common.MW}`}
                  onChange={setSolarEnergy}
                  displayScale
                />

                <SliderTitle>
                  <WindIcon color={'var(--green)'} />
                  {ENERGY_TYPE.WIND}
                </SliderTitle>

                <RangeSlider
                  id="wind_energy"
                  min={0}
                  max={maxPower.max_wind_installation_power}
                  value={windEnergy}
                  step={0.1}
                  label={` ${copy.common.MW}`}
                  onChange={setWindEnergy}
                  displayScale
                />

                <StyledButton type="submit" disabled={loading || !areEnergyValuesValid}>
                  {loading ? <Spinner size={16} /> : text.recalculate_offer}
                </StyledButton>
              </form>
            </OfferInfoWrapper>

            {matchData.yearly_data && (
              <YearSummary
                data={matchData.yearly_data}
                onShowZones={openZonesModal}
                style={{ minWidth: '50%' }}
              />
            )}
          </Info>
        )}

        {matchData?.monthly_data && (
          <MonthlyDataGraph data={matchData.monthly_data} onShowZones={openZonesModal} />
        )}
      </Content>

      <ZonesModal
        isOpen={isZonesModalOpen}
        zones={zones}
        onClose={() => setIsZonesModalOpen(false)}
      />

      {/* Consumption details */}
      {businessId && (
        <ConsumptionDetailsModal
          demandId={businessId}
          isModalOpen={consumptionDetailsModalOpen}
          onModalClose={() => setConsumptionDetailsModalOpen(false)}
        />
      )}
    </>
  )
}

const Content = styled(PageContainer)`
  gap: 24px;
  flex-wrap: wrap;
  padding-bottom: 64px;

  h3 {
    font: var(--600-16);
  }

  a {
    color: inherit;
  }
`

const Info = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 24px;
`

const OfferInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 280px;
`

const SliderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  margin-top: 22px;
  padding-bottom: 5px;
  font: var(--600-16);
`

const StyledButton = styled(Button)`
  width: 100%;
  max-width: 289px;
  margin-top: 15px;
`

const NoDemands = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  text-align: center;

  h2 {
    font: var(--600-24);
  }

  p {
    max-width: 415px;
    font: var(--400-15);
  }
`
